import { updateSeller, useSeller, type SellerForm as SellerFormValues } from '@gozoki/api'
import type { Seller } from '@gozoki/api-types'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import z from 'zod/v3'
import { SellerForm } from '~/components/routes/sellers/SellerForm'
import { Spinner } from '~/components/ui/spinner'
import { getAxiosErrorMessage } from '~/lib/utils'
import { useRights } from '~/utils/hooks'
import { withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/sellers/$sellerId/edit')({
  staticData: {
    permission: ['read', 'sellers'],
  },
  params: {
    parse: z.object({ sellerId: z.coerce.number() }).parse,
  },
  component: EditSellerPage,
})
withRoutePermissions(Route)

const toFormValues = (seller: Seller): SellerFormValues => ({
  reference: seller.reference ?? '',
  label: seller.label,
  address: seller.address?.address ?? '',
  addressAdditionnal: seller.address?.addressAdditionnal ?? '',
  postalCode: seller.address?.postalCode ?? '',
  city: seller.address?.city ?? '',
})

function EditSellerPage() {
  const { sellerId } = Route.useParams()
  const navigate = useNavigate()
  const isUserAdmin = useRights(['ADMIN'])

  const { data: seller, isLoading } = useSeller(sellerId)
  const { mutateAsync: update } = useMutation({ mutationFn: updateSeller })

  const navigateBack = () => navigate({ to: '/sellers/sellers' })

  if (isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner />
      </div>
    )
  }

  if (!seller) {
    return <div className="text-muted-foreground p-16 text-center">Vendeur introuvable.</div>
  }

  return (
    <SellerForm
      key={seller.id}
      title={isUserAdmin ? 'Modifier un vendeur' : 'Voir un vendeur'}
      submitLabel="Modifier"
      canEdit={isUserAdmin}
      defaultValues={toFormValues(seller)}
      onCancel={navigateBack}
      onSubmit={async (values) => {
        try {
          await update({ id: sellerId, data: values })
          navigateBack()
        } catch (error) {
          toast.error(getAxiosErrorMessage(error))
        }
      }}
    />
  )
}
